import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { OrderingScene } from '@/types/gameSpec';
import { shuffle, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

/**
 * Up/down arrows to reorder a shuffled list. No drag-drop needed —
 * works on touch + keyboard out of the box.
 */
export const OrderingSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<OrderingScene>) => {
  // Track original indices; correct order is [0, 1, 2, ...]
  const [order, setOrder] = useState<number[]>(() =>
    shuffle(scene.items.map((_, i) => i)),
  );
  const [revealed, setRevealed] = useState(false);

  const move = (from: number, dir: -1 | 1) => {
    if (revealed) return;
    const to = from + dir;
    if (to < 0 || to >= order.length) return;
    const next = order.slice();
    [next[from], next[to]] = [next[to], next[from]];
    setOrder(next);
  };

  const submit = () => {
    if (revealed) return;
    setRevealed(true);
    const ok = order.every((origIdx, i) => origIdx === i);
    setTimeout(() => onAnswer(ok, { order }), 900);
  };

  // shuffled in case user solved by accident on first render
  const isSolvedNow = useMemo(() => order.every((v, i) => v === i), [order]);

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.ordering}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        <ol className="space-y-2">
          {order.map((origIdx, pos) => {
            const correct = revealed && origIdx === pos;
            const wrong = revealed && origIdx !== pos;
            return (
              <li
                key={origIdx}
                className={cn(
                  'flex items-center gap-2 rounded-md border bg-card px-3 py-2',
                  correct && 'border-green-500 bg-green-50',
                  wrong && 'border-destructive bg-red-50',
                )}
              >
                <span className="text-sm text-muted-foreground w-5">
                  {pos + 1}.
                </span>
                <span className="flex-1">{scene.items[origIdx]}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => move(pos, -1)}
                  disabled={revealed || pos === 0}
                  aria-label="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => move(pos, 1)}
                  disabled={revealed || pos === order.length - 1}
                  aria-label="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ol>

        {!revealed && (
          <Button onClick={submit}>
            Submit order {isSolvedNow ? '(looks right!)' : ''}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
